<docs>
The search field: a native search input with a magnifier, a clear button, and an optional joined
filter dropdown that scopes the query. A search box, not an autocomplete: it suggests nothing while
typing and acts on submit. For type-ahead suggestions use ZoneFloatingMenu with `searchable`; to
filter the rows of a ZoneTable use the table's own search; a plain text field is ZoneTextInput (its
`type="search"` was removed once this component shipped, so every search field is one of these).

## v-model

`v-model` is the query String; `v-model:filter` is the chosen filter's id (Filtered type only).
With `filters` set but `filter` unset, the first filter is treated as the active scope: the button,
the menu's radio and `filterName` all agree on it.

## Notes

- Keyboard, per the Figma spec: Enter emits `search` and is not prevented, so a surrounding Rails
  form still submits natively. Escape clears the field, but only when it has a value; on an empty
  field the key is left to bubble so it can still close a surrounding modal or menu.
- Accessible name: pass `label` (or `#label`) for a visible one. With neither, the `placeholder` is
  used as `aria-label` so the field is never unnamed; pass `ariaLabel` to override that. A visible
  label wins and no `aria-label` is emitted, since it would otherwise shadow the label.
- `name` sits on the native input, so it submits in a Rails form with no hidden input. The filter is
  the exception: it needs `filterName` to submit.
- `inheritAttrs: false`: extra attrs and DOM listeners (autofocus, inputmode, `@blur`, `@keydown`…)
  land on the `<input>`, not the wrapper.
- The browser's own search-cancel × is suppressed; the clear button is Zone's. Clicking anywhere in
  the bordered box focuses the input, so the padding and the magnifier are not dead zones.
- Focus draws a 2px blue edge as border + ring rather than `border-2`, so the field does not shift by
  a pixel when focused.
- The filter dropdown is a ZoneFloatingMenu, so its panel teleports to `#zone-teleport`. In tests,
  query `document.body` for the rows, not the wrapper.

Figma: [Search input](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=3199-59381&m=dev)
</docs>

<template>
  <div class="flex flex-col gap-2">
    <!--
      Label row. #label REPLACES the standard ZoneLabel rather than rendering inside it, so callers
      can compose their own row without ending up with a <label> nested in a <label>.
    -->
    <slot
      v-if="$slots.label"
      name="label"
      :html-for="resolvedId"
      :required="required"
      :invalid="invalid"
      :tooltip="tooltip"
    />
    <ZoneLabel
      v-else-if="label || tooltip"
      :label="label"
      :required="required"
      :tooltip="tooltip"
      :icon="Boolean(tooltip)"
      :invalid="invalid"
      :html-for="resolvedId"
    />

    <div
      data-testid="search-input-row"
      :class="rowClasses"
    >
      <slot
        v-if="$slots.filter"
        name="filter"
        :classes="filterClasses"
        :disabled="disabled"
        :size="size"
        :variant="variant"
      />
      <ZoneFloatingMenu
        v-else-if="hasFilters"
        selectable="single"
        :items="filters"
        :selected="resolvedFilterId"
        :disabled="disabled"
        @update:selected="onFilterSelect"
      >
        <template #trigger>
          <button
            data-testid="search-input-filter"
            type="button"
            :class="filterClasses"
            :disabled="disabled"
            :aria-label="t('filter_by')"
          >
            <span class="text-sm leading-5 text-gray-600 whitespace-nowrap">{{ filterButtonLabel }}</span>
            <ZoneIcon
              icon="expand_more"
              class="text-gray-500"
            />
          </button>
        </template>
      </ZoneFloatingMenu>

      <div
        data-testid="search-input-body"
        :class="bodyClasses"
        @click="focusInput"
      >
        <span
          v-if="icon"
          class="flex h-full shrink-0 items-center px-1.5 text-gray-500 pointer-events-none"
          aria-hidden="true"
        >
          <ZoneIcon icon="search" />
        </span>

        <input
          :id="resolvedId"
          ref="inputEl"
          type="search"
          :class="inputClasses"
          :name="name"
          :value="modelValue"
          :placeholder="placeholder"
          :disabled="disabled"
          :required="required"
          :aria-label="inputAriaLabel"
          :aria-invalid="invalid || undefined"
          v-bind="$attrs"
          @input="onInput"
          @keydown="onKeydown"
        >

        <span
          v-if="showClear"
          class="flex h-full shrink-0 items-center px-1.5"
        >
          <button
            type="button"
            class="flex size-5 items-center justify-center rounded-full text-black hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            :aria-label="t('clear')"
            @click="clear"
          >
            <ZoneIcon icon="close" />
          </button>
        </span>
      </div>
    </div>

    <input
      v-if="filterName"
      type="hidden"
      :name="filterName"
      :value="resolvedFilterId ?? ''"
      :disabled="disabled"
    >

    <slot name="notice" />
  </div>
</template>

<script lang="ts">
import { computed, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon, { type IconName } from './Icon.vue';
import ZoneFloatingMenu from './FloatingMenu.vue';
import ZoneLabel from './Label.vue';

const SIZES = ['small', 'base', 'large'] as const;

const VARIANTS = ['round', 'square'] as const;

export type SearchInputSize = (typeof SIZES)[number];

export type SearchInputVariant = (typeof VARIANTS)[number];

export type SearchInputFilterId = string | number;

// A type alias, not an interface: these are handed straight to ZoneFloatingMenu's `items`, whose
// FloatingMenuItem carries an `[key: string]: unknown` index signature for the extra keys a caller
// may attach. TypeScript grants an implicit index signature to object type aliases but never to
// interfaces, so as an interface this shape is not assignable to FloatingMenuItem at all.
export type SearchInputFilter = {
  id: SearchInputFilterId;
  label: string;
  /** A Zone icon name (see ZoneIcon), drawn before the label in the dropdown. */
  icon?: IconName;
  disabled?: boolean;
};

// Field heights straight from the Figma size table (Small 32 / Base 40 / Large 48px), with the
// body's inset padding scaling alongside them. Every size keeps the 14px body-sm type scale.
const SIZE_CLASSES: Record<SearchInputSize, { row: string; body: string; filter: string }> = {
  small: { row: 'h-8', body: 'p-1', filter: 'px-4' },
  base: { row: 'h-10', body: 'p-2', filter: 'px-5' },
  large: { row: 'h-12', body: 'p-2.5', filter: 'px-6' },
};

// Per-instance fallback id, so the visible label can be `for`-associated with the field even when
// the caller doesn't pass an explicit `id`.
let uid = 0;
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneSearchInput', inheritAttrs: false });

const { t } = useI18n();

const props = defineProps({
  /** The query. */
  modelValue: {
    type: String,
    default: '',
  },
  /** Set on the native input: submits in a Rails form with no hidden input. */
  name: {
    type: String,
    default: null,
  },
  /** Wired to the label's `for`; falls back to a generated id. */
  id: {
    type: String,
    default: null,
  },
  /** Also the `aria-label` when there is no visible label and no `ariaLabel`. */
  placeholder: {
    type: String,
    default: null,
  },
  /** 32 / 40 / 48px tall. */
  size: {
    type: String as PropType<SearchInputSize>,
    default: 'base',
    validator: (v: string) => SIZES.includes(v as SearchInputSize),
  },
  /** Figma calls `square` "unlikely to be used": reach for it only to match a squared form. */
  variant: {
    type: String as PropType<SearchInputVariant>,
    default: 'round',
    validator: (v: string) => VARIANTS.includes(v as SearchInputVariant),
  },
  /**
   * The leading magnifier. Figma's guidance is to keep it: it is the universal signifier that a
   * field is a search.
   */
  icon: {
    type: Boolean,
    default: true,
  },
  /** The trailing × that resets the field, shown once there is something to clear. */
  clearable: {
    type: Boolean,
    default: true,
  },
  /**
   * A non-empty array switches the component to Figma's "Filtered" type (there is no `type` prop);
   * empty renders the "Simple" type. Items are `{ id, label, icon?, disabled? }`.
   */
  filters: {
    type: Array as PropType<SearchInputFilter[]>,
    default: () => [],
  },
  /** v-model:filter, the chosen filter's id. Unset means the first filter is the active scope. */
  filter: {
    type: [String, Number] as PropType<SearchInputFilterId | null>,
    default: null,
  },
  /** Overrides the filter button's text without changing which filter is selected. */
  filterLabel: {
    type: String,
    default: null,
  },
  /** Submits the chosen filter id in a hidden input, for a Rails form. */
  filterName: {
    type: String,
    default: null,
  },
  /** Label row text (the `#label` slot replaces the row). */
  label: {
    type: String,
    default: null,
  },
  /** Info-tooltip text on the label row. */
  tooltip: {
    type: String,
    default: null,
  },
  /** Accessible name when there is no visible label. Falls back to the placeholder. */
  ariaLabel: {
    type: String,
    default: null,
  },
  /** Asterisk in the label row plus the native `required` attribute. */
  required: {
    type: Boolean,
    default: false,
  },
  /** The whole field at 30% opacity plus the native `disabled` attribute. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Red border and `aria-invalid` on the field. */
  invalid: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /** On every keystroke, and `''` on clear. */
  'update:modelValue': [value: string];
  /** A filter was chosen. */
  'update:filter': [id: SearchInputFilterId];
  /** Enter was pressed, with the current query. */
  search: [query: string];
  /** Cleared via the × button or Escape. */
  clear: [];
}>();

const slots = defineSlots<{
  /**
   * Replaces the whole label row; it does not render inside the built-in ZoneLabel, so render your own
   * ZoneLabel in it to keep the standard look. The `label`/`tooltip` props are not rendered meanwhile.
   */
  label?(props: { htmlFor: string; required: boolean; invalid: boolean; tooltip: string | null }): any;
  /**
   * Replaces the built-in filter segment, for a scope control this component does not model (a ZoneSelect,
   * a multi-select ZoneFloatingMenu, two buttons…). Spread `classes` onto your own single root element to
   * inherit the joined border, radius and height.
   */
  filter?(props: { classes: string; disabled: boolean; size: SearchInputSize; variant: SearchInputVariant }): any;
  /** Free-form content below the field, for error/helper text. Unstyled. */
  notice?(): any;
}>();

const inputEl = ref<HTMLInputElement | null>(null);

const generatedId = `zone-search-input-${(uid += 1)}`;

// Explicit `id` if given, otherwise a stable per-instance id, so the label's `for` and the
// field's `id` always match.
const resolvedId = computed<string>(() => props.id || generatedId);

const hasFilters = computed<boolean>(() => props.filters.length > 0);

// A scope filter always has an active scope, so an unset `filter` reads as the first one.
// Button text, the menu's radio and the hidden input all resolve through here, so they agree.
const resolvedFilterId = computed<SearchInputFilterId | null>(() => {
  if (props.filter !== null && props.filter !== undefined) {
    return props.filter;
  }

  return props.filters[0]?.id ?? null;
});

const filterButtonLabel = computed<string>(() => {
  if (props.filterLabel) {
    return props.filterLabel;
  }

  const active = props.filters.find((entry) => entry.id === resolvedFilterId.value);

  return active?.label ?? '';
});

const hasValue = computed<boolean>(() => String(props.modelValue ?? '').length > 0);

const showClear = computed<boolean>(() => props.clearable && hasValue.value && !props.disabled);

// A visible label already names the field; emitting aria-label too would shadow it.
const placeholder = computed<string>(() => props.placeholder ?? t('placeholder'));
const hasVisibleLabel = computed<boolean>(() => Boolean(props.label || slots.label));

const inputAriaLabel = computed<string | undefined>(() => {
  if (props.ariaLabel) {
    return props.ariaLabel;
  }
  if (hasVisibleLabel.value) {
    return undefined;
  }

  return placeholder.value || undefined;
});

const rowClasses = computed<string>(() => [
  'relative flex items-stretch',
  SIZE_CLASSES[props.size].row,
  // The two segments overlap by a pixel, so they need a stacking context of their own.
  hasFilters.value || slots.filter ? 'isolate' : '',
  props.disabled ? 'opacity-30 cursor-not-allowed' : '',
].filter(Boolean).join(' '));

// Fully rounded in the round variant, 6px in square; only the trailing corners when a filter
// segment owns the leading ones.
const bodyRadiusClasses = computed<string>(() => {
  const joined = hasFilters.value || Boolean(slots.filter);

  if (props.variant === 'square') {
    return joined ? 'rounded-l-none rounded-r-md' : 'rounded-md';
  }

  return joined ? 'rounded-l-none rounded-r-full' : 'rounded-full';
});

const bodyClasses = computed<string>(() => {
  // z-[1] sits under the filter segment so the shared border reads as the segment's; on focus
  // the body jumps to z-[3] so its 2px blue edge is not cut off where the two meet.
  const classes = [
    'relative z-[1] focus-within:z-[3] flex min-w-0 flex-1 items-center border border-solid bg-white',
    SIZE_CLASSES[props.size].body,
    bodyRadiusClasses.value,
  ];

  // Figma draws focus as a 2px primary border. Growing the border would shift the layout by a
  // pixel, so the second pixel is a ring instead.
  if (props.invalid) {
    classes.push('border-red-500 focus-within:ring-1 focus-within:ring-red-500');
  } else {
    classes.push('border-gray-300 focus-within:border-primary-700 focus-within:ring-1 focus-within:ring-primary-700');
  }

  return classes.join(' ');
});

const filterClasses = computed<string>(() => {
  const radius = props.variant === 'square' ? 'rounded-l-md' : 'rounded-l-full';

  return [
    // -mr-px collapses the two adjacent 1px borders into one, and z-[2] keeps this segment's
    // border on top of the body's where they meet.
    'relative z-[2] -mr-px flex shrink-0 items-center gap-2 border border-solid border-gray-300 bg-white',
    SIZE_CLASSES[props.size].filter,
    radius,
    props.disabled ? 'cursor-not-allowed' : '',
  ].filter(Boolean).join(' ');
});

const inputClasses = computed<string>(() => [
  'h-full min-w-0 flex-1 border-0 bg-transparent px-1.5 text-sm leading-5 text-gray-900',
  // placeholder:font-regular pins the placeholder to 400. The scoped preflight gives
  // `font-weight: inherit` to every input/textarea/select under `.w-tw`, so the field —
  // and with it ::placeholder — takes the weight of whatever ancestor is in effect; drop
  // this and any `font-medium` wrapper silently bolds the placeholder. Every Zone field
  // with a real ::placeholder needs it; see llms.txt.
  'placeholder:font-regular placeholder:text-gray-400 outline-none focus:border-0 focus:outline-none focus:ring-0',
  // The browser's own search-cancel affordance would sit next to Zone's clear button.
  '[&::-webkit-search-cancel-button]:appearance-none',
  props.disabled ? 'cursor-not-allowed' : '',
].filter(Boolean).join(' '));

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}

function clear() {
  emit('update:modelValue', '');
  emit('clear');
  inputEl.value?.focus();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    // Deliberately not prevented: a surrounding Rails form should still submit natively.
    emit('search', String(props.modelValue ?? ''));

    return;
  }

  if (event.key !== 'Escape' || !hasValue.value) {
    // An Escape on an empty field is left to bubble, so it can still close a surrounding
    // modal or menu rather than being swallowed by a field with nothing to clear.
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  clear();
}

function onFilterSelect(id: SearchInputFilterId) {
  emit('update:filter', id);
}

// The bordered box is wider than the input itself — its padding and the magnifier would
// otherwise be dead zones that swallow a click aimed at the field.
function focusInput() {
  if (props.disabled) {
    return;
  }

  inputEl.value?.focus();
}
</script>

<i18n lang="json">
{
  "en": {
    "placeholder": "Search",
    "filter_by": "Filter search by",
    "clear": "Clear search"
  }
}
</i18n>
